import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { getKafLesson, markKafLessonProgress } from "@/lib/kaf-academy.functions";
import { LivingBackdrop } from "@/academy/engines/living/LivingBackdrop";
import { KafLessonBlocks } from "@/academy/kaf/LessonBlocks";
import { ArrowLeft, Check } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import {
  NOINDEX_META, breadcrumbs, isoMinutes, ldScript, learningResourceNode, seoMeta,
} from "@/lib/schema";
import { absoluteUrl } from "@/lib/site";

export const Route = createFileRoute("/academy/kafrequency/$domain/lesson/$lesson")({
  loader: async ({ params }) => {
    const data = await getKafLesson({ data: { slug: params.lesson } });
    if (!data) throw notFound();
    return data;
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Lesson unavailable — KAfrequency Academy" }, NOINDEX_META] };
    }
    const l = loaderData.lesson;
    const path = `/academy/kafrequency/${params.domain}/lesson/${params.lesson}`;
    const title = `${l.title} — KAfrequency Academy | Buddha KA`;
    const description = l.summary ?? `${l.title}: a lesson from the KAfrequency Academy.`;
    return {
      ...seoMeta({ title, description, path, ogType: "article" }),
      scripts: [
        ldScript(
          learningResourceNode({
            id: `${absoluteUrl(path)}#lesson`,
            url: absoluteUrl(path),
            name: l.title,
            description,
            partOfCourseId: `${absoluteUrl(`/academy/kafrequency/${params.domain}`)}#course`,
            educationalLevel: l.difficulty ?? undefined,
            timeRequired: isoMinutes(l.est_minutes),
          }),
        ),
        ldScript(
          breadcrumbs([
            ["Academy", "/academy"],
            ["KAfrequency", "/academy/kafrequency"],
            [params.domain, `/academy/kafrequency/${params.domain}`],
            [l.title, path],
          ]),
        ),
      ],
    };
  },

  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <h1 className="font-serif text-3xl">Lesson not found</h1>
      <Link to="/academy/kafrequency" className="mt-4 inline-block text-gold">Back to the spiral</Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <p className="text-sm text-foreground/60">{(error as Error).message}</p>
    </div>
  ),
  component: LessonPage,
});

function LessonPage() {
  const initial = Route.useLoaderData();
  const params = Route.useParams();
  const { data } = useQuery({
    queryKey: ["kaf", "lesson", initial.lesson.slug],
    queryFn: () => getKafLesson({ data: { slug: initial.lesson.slug } }),
    initialData: initial,
  });
  const mark = useServerFn(markKafLessonProgress);
  const [signedIn, setSignedIn] = useState(false);
  const [saved, setSaved] = useState<"idle" | "saving" | "done">("idle");

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setSignedIn(!!data.user));
  }, []);

  if (!data) return null;
  const { lesson, domain } = data;
  const hue = domain?.hue ?? 44;

  async function complete() {
    if (!signedIn) return;
    setSaved("saving");
    try {
      await mark({ data: { lessonId: lesson.id, status: "completed" } });
      setSaved("done");
    } catch {
      setSaved("idle");
    }
  }

  return (
    <div className="relative min-h-dvh bg-obsidian text-foreground">
      <div className="absolute inset-0 opacity-60"><LivingBackdrop hue={hue} density={70} /></div>
      <article className="relative z-10 mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <Link
          to="/academy/kafrequency/$domain"
          params={{ domain: params.domain }}
          className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.3em] text-gold/80 hover:text-gold"
        >
          <ArrowLeft size={12} /> {domain?.name ?? "Domain"}
        </Link>
        <h1 className="mt-4 font-serif text-3xl sm:text-4xl text-gold-luminous">{lesson.title}</h1>
        {lesson.summary && <p className="mt-2 text-foreground/75">{lesson.summary}</p>}
        <div className="mt-3 flex items-center gap-3 text-xs uppercase tracking-widest text-foreground/60">
          <span>{lesson.difficulty}</span>
          <span>· {lesson.est_minutes} min</span>
        </div>

        <div className="mt-8">
          <KafLessonBlocks blocks={lesson.body ?? []} hue={hue} />
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          {signedIn ? (
            <button
              onClick={complete}
              disabled={saved !== "idle"}
              className="inline-flex items-center gap-2 rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian disabled:opacity-60"
            >
              <Check size={14} /> {saved === "done" ? "Marked complete" : saved === "saving" ? "Saving…" : "Mark complete"}
            </button>
          ) : (
            <Link to="/auth" className="rounded-full border hairline px-5 py-2.5 text-sm">
              Sign in to track progress
            </Link>
          )}
          <Link to="/academy/kafrequency/$domain" params={{ domain: params.domain }} className="text-sm text-gold hover:underline">
            Back to {domain?.name ?? "domain"}
          </Link>
        </div>
      </article>
    </div>
  );
}
